import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { API_URL, formatApiError } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger,
} from '../ui/sheet';
import {
  Sparkle, PaperPlaneRight, Spinner, ArrowsClockwise, User,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const SUGGESTIONS = [
  'How many subscribers do we have, by performance band?',
  'Show me the top 5 subscribers by wallet balance.',
  'What is the wallet balance on card 1370004298477?',
  'Last 5 transactions on card 1370004298477.',
];

export default function AskEcowaterAI({ entityId }) {
  const { isSuperAdmin, user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId] = useState(`ai-${Date.now()}`);
  const scrollerRef = useRef(null);

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const ask = async (text) => {
    const q = (text || '').trim();
    if (!q || sending) return;
    const newMessages = [...messages, { role: 'user', content: q }];
    setMessages(newMessages);
    setInput('');
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/ai/chat`,
        {
          messages: newMessages,
          session_id: sessionId,
          entity_id: isSuperAdmin && entityId && entityId !== 'all' ? entityId : null,
        },
        { headers: { ...authHeaders(), 'Content-Type': 'application/json' }, timeout: 60000 },
      );
      const reply = String(data?.reply || '').trim() || 'No response.';
      setMessages((m) => [...m, { role: 'assistant', content: reply }]);
    } catch (e) {
      const msg = formatApiError(e, 'AI is unavailable right now');
      setMessages((m) => [...m, { role: 'assistant', content: `_${msg}_`, error: true }]);
      toast.error(msg);
    } finally { setSending(false); }
  };

  const reset = () => {
    setMessages([]);
    setInput('');
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          data-testid="ask-ai-launcher"
          className="fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 h-12 pl-3 pr-4 rounded-full bg-[#0A2540] text-white shadow-[0_12px_30px_-10px_rgba(10,37,64,0.5)] hover:scale-105 transition-transform"
        >
          <span className="h-8 w-8 rounded-full bg-[#C9A35B] flex items-center justify-center">
            <Sparkle size={16} weight="fill" className="text-white" />
          </span>
          <span className="text-sm font-semibold tracking-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Ask Ecowater AI
          </span>
        </button>
      </SheetTrigger>

      <SheetContent
        side="right"
        className="w-full sm:max-w-lg p-0 bg-[#FCFAF6] border-l border-[#EDE6D8] flex flex-col"
        data-testid="ask-ai-drawer"
      >
        <SheetHeader className="text-left p-5 border-b border-[#EDE6D8] bg-white">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-[#0A2540] flex items-center justify-center">
                <Sparkle size={18} weight="fill" className="text-[#C9A35B]" />
              </div>
              <div>
                <SheetTitle className="text-[#1C1917] text-lg" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Ask Ecowater AI
                </SheetTitle>
              </div>
            </div>
            {messages.length > 0 && (
              <button
                type="button"
                onClick={reset}
                title="New conversation"
                className="h-8 w-8 rounded-lg border border-[#EDE6D8] bg-white hover:bg-[#FCFAF6] flex items-center justify-center"
              >
                <ArrowsClockwise size={14} className="text-[#0A2540]" />
              </button>
            )}
          </div>
        </SheetHeader>

        {/* Chat area */}
        <div
          ref={scrollerRef}
          data-testid="ask-ai-messages"
          className="flex-1 overflow-y-auto px-5 py-4 space-y-4"
        >
          {messages.length === 0 ? (
            <div className="text-center pt-6 pb-2">
              <Sparkle size={28} weight="duotone" className="text-[#C9A35B] mx-auto" />
              <p className="text-sm text-[#1C1917] font-semibold mt-3">Hi {(user?.fullname || user?.username || 'there').split(/\s+/)[0]} — ask me anything about your data.</p>
              <p className="text-[11px] text-[#78716C] mt-1 px-4">
                I can look up subscribers, wallet balances, transactions, and performance bands. Ask me anything about your subscriber base.
              </p>
              <div className="mt-5 space-y-2">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => ask(s)}
                    data-testid={`ai-suggestion-${s.slice(0, 20).replace(/\s+/g, '-').toLowerCase()}`}
                    className="block w-full text-left text-[12px] text-[#0A2540] bg-white border border-[#EDE6D8] hover:border-[#C9A35B] rounded-lg px-3 py-2 transition"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((m, i) => <ChatBubble key={i} role={m.role} content={m.content} error={m.error} />)
          )}
          {sending && (
            <div className="flex items-center gap-2 text-[12px] text-[#78716C] pl-11">
              <Spinner size={12} className="animate-spin" />
              Thinking…
            </div>
          )}
        </div>

        {/* Composer */}
        <form
          onSubmit={(e) => { e.preventDefault(); ask(input); }}
          className="border-t border-[#EDE6D8] bg-white p-3"
        >
          <div className="flex items-end gap-2">
            <textarea
              data-testid="ask-ai-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  ask(input);
                }
              }}
              rows={1}
              placeholder="Ask about subscribers, recharges, campuses…"
              className="flex-1 resize-none rounded-lg border border-[#EDE6D8] bg-[#FCFAF6] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0A2540]/20 max-h-32"
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              data-testid="ask-ai-send"
              className="h-10 w-10 rounded-lg bg-[#0A2540] text-white flex items-center justify-center disabled:opacity-40 hover:bg-[#0F2D55] transition"
            >
              {sending
                ? <Spinner size={14} className="animate-spin" />
                : <PaperPlaneRight size={14} weight="fill" />}
            </button>
          </div>
          <p className="text-[10px] text-[#A8A29E] mt-2 pl-1">
            Answers come from your live data.
          </p>
        </form>
      </SheetContent>
    </Sheet>
  );
}

function ChatBubble({ role, content, error }) {
  if (role === 'user') {
    return (
      <div className="flex items-start gap-2 justify-end">
        <div className="max-w-[80%] bg-[#0A2540] text-white rounded-2xl rounded-br-md px-4 py-2.5 text-sm leading-relaxed">
          {content}
        </div>
        <div className="h-8 w-8 rounded-full bg-[#FCFAF6] border border-[#EDE6D8] flex items-center justify-center shrink-0">
          <User size={13} className="text-[#0A2540]" />
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-2">
      <div className="h-8 w-8 rounded-full bg-[#0A2540] flex items-center justify-center shrink-0">
        <Sparkle size={13} weight="fill" className="text-[#C9A35B]" />
      </div>
      <div
        className={`max-w-[80%] rounded-2xl rounded-bl-md px-4 py-2.5 text-sm leading-relaxed ${
          error ? 'bg-[#FBE7E7] text-[#B91C1C] border border-[#FBE7E7]' : 'bg-white border border-[#EDE6D8] text-[#1C1917]'
        }`}
      >
        <div className="ai-markdown space-y-2 [&_p]:m-0 [&_table]:w-full [&_table]:text-xs [&_th]:text-left [&_th]:font-semibold [&_th]:py-1 [&_td]:py-1 [&_td]:border-t [&_td]:border-[#EDE6D8] [&_strong]:font-semibold [&_strong]:text-[#0A2540] [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5">
          <ReactMarkdown>{content}</ReactMarkdown>
        </div>
      </div>
    </div>
  );
}
